import InputField from '~/components/ui/Forms/InputField';
import { getLocalizedString } from '~/i18n/utils';
import {
  ACCESS_TOKEN_UNIT_OPTIONS,
  DEFAULT_ACCESS_TOKEN_EXPIRY_UNIT,
  getPermissionForEditingEnforce_PKCE,
  getPermissionsForShowingEnforce_PKCE,
  getPermissionsForShowingSKI_API_Access,
  getTranslationKey,
  getZodApplicationsDetailsSchema,
  handleEditApplicationErrors
} from './utils';
import Checkbox from '~/components/ui/Checkbox';
import { Localized } from '~/i18n';
import { ark } from '@ark-ui/solid';
import SubmitButton from '~/components/Form/SubmitButton';
import { Button } from '~/components/ui/Button';
import Form from '~/components/Form';
import {
  ApiConfigData,
  ApiConfigUpdateRequest,
  ApplicationTypeEnum,
  getEnvironmentMultiApplicationDetailQueryKey,
  getEnvironmentMultiApplicationListQueryKey,
  updateClientCredentials
} from '~/api';
import { useIntl } from '@cookbook/solid-intl';
import {
  convertMinsToSec,
  convertSecsToMins,
  preventInvalidKeyPress
} from '~/utils/commonUtils';
import { validator } from '@felte/validator-zod';
import { useQueryClient } from '@tanstack/solid-query';
import { useToast } from '~/components/ui/Toast';
import { mergeProps, Show } from 'solid-js';
import InputMenuField from '~/components/ui/Forms/InputMenuField';
import { useFormContext } from '~/components/Form/FormContext';

type ApplicationDetailsForm = {
  applicationData: ApiConfigData;
};

function ApplicationDetailsForm(props: ApplicationDetailsForm) {
  const merged = mergeProps({}, props);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const toaster = useToast();
  const formContext = useFormContext();

  const getInitialValues = () => {
    return {
      name: merged.applicationData.name,
      allowApiAccess: !merged.applicationData.disallowScalekitApiAccess,
      enforcePKCE: merged.applicationData.enforcePkce,
      accessTokenExpiry: merged.applicationData.accessTokenExpiry
        ? convertSecsToMins(merged.applicationData.accessTokenExpiry)
        : '',
      accessTokenExpiryUnit: DEFAULT_ACCESS_TOKEN_EXPIRY_UNIT
    };
  };

  const handleSubmitForm = async (data) => {
    const { name, accessTokenExpiry, allowApiAccess, enforcePKCE } = data;

    const payload: ApiConfigUpdateRequest = {
      name,
      accessTokenExpiry: convertMinsToSec(accessTokenExpiry).toString(),
      disallowScalekitApiAccess: !allowApiAccess,
      enforcePkce: enforcePKCE
    };

    return await updateClientCredentials(merged.applicationData.keyId, payload);
  };

  const handleSuccessApplicationUpdate = async (data) => {
    const localData = data as ApiConfigData;

    queryClient.refetchQueries({
      queryKey: getEnvironmentMultiApplicationListQueryKey(),
      type: 'all'
    });

    queryClient.refetchQueries({
      queryKey: getEnvironmentMultiApplicationDetailQueryKey(localData.keyId),
      type: 'all'
    });

    toaster.toast().success({
      description: getLocalizedString(
        getTranslationKey('toast_message_application_edited_successfully'),
        {
          intl,
          applicationName: localData.name
        }
      )
    });
  };

  return (
    <div>
      <Form
        initialValues={getInitialValues()}
        extend={validator({
          schema: getZodApplicationsDetailsSchema(intl)
        })}
        onSubmit={handleSubmitForm}
        onSuccess={handleSuccessApplicationUpdate}
        errorHandler={handleEditApplicationErrors}
      >
        {({ data, setFields, isChanged, reset }) => {
          return (
            <>
              <div class="max-w-[700px]">
                <div class={`mb-6`}>
                  <InputField
                    name={'name'}
                    label={getLocalizedString(
                      getTranslationKey(`config_application_name_input_label`)
                    )}
                    placeholder={getLocalizedString(
                      getTranslationKey(
                        `config_application_name_input_placeholder`
                      )
                    )}
                    dataTestId="multi-app-name-input"
                  />
                </div>
              </div>

              <Show
                when={getPermissionsForShowingSKI_API_Access(
                  merged.applicationData.clientType as ApplicationTypeEnum
                )}
              >
                <div class={`mb-6`}>
                  <Checkbox
                    checked={data().allowApiAccess}
                    size={'sm'}
                    id={`multi-app-allow-sk-api-access`}
                    onCheckedChange={({ checked }) => {
                      setFields('allowApiAccess', checked);
                    }}
                    data-testid={`multi-app-allow-sk-api-access-checkbox`}
                  >
                    <Localized
                      translationKey={getTranslationKey(
                        `config_checkbox_allow_api_access_label`
                      )}
                    />
                  </Checkbox>
                  <div class={`pl-6 text-sm mt-1 text-fg-subtle`}>
                    <Localized
                      translationKey={getTranslationKey(
                        `config_checkbox_allow_api_access_hint`
                      )}
                    />
                  </div>
                </div>
              </Show>

              <Show
                when={getPermissionsForShowingEnforce_PKCE(
                  merged.applicationData.clientType as ApplicationTypeEnum
                )}
              >
                <div class={`mb-6`}>
                  <Checkbox
                    checked={data().enforcePKCE}
                    disabled={
                      !getPermissionForEditingEnforce_PKCE(
                        merged.applicationData.clientType as ApplicationTypeEnum
                      )
                    }
                    size={'sm'}
                    id={`multi-app-enforce-pkce`}
                    onCheckedChange={({ checked }) => {
                      setFields('enforcePKCE', checked);
                    }}
                    data-testid={`multi-app-enforce-pkce-checkbox`}
                  >
                    <Localized
                      translationKey={getTranslationKey(
                        `config_checkbox_enforce_pkce_label`
                      )}
                    />
                  </Checkbox>
                  <div class={`pl-6 text-sm mt-1 text-fg-subtle`}>
                    <Localized
                      translationKey={getTranslationKey(
                        `config_checkbox_enforce_pkce_hint`
                      )}
                    />
                  </div>
                </div>
              </Show>

              <div class={`min-w-[800px]`}>
                <ark.label class={`text-fg-default mb-1 font-medium text-sm`}>
                  {getLocalizedString(
                    getTranslationKey(`config_access_token_exp_label`)
                  )}
                </ark.label>
                <p class={`text-sm mb-1.5 text-fg-subtle`}>
                  {getLocalizedString(
                    getTranslationKey(`config_access_token_exp_hint`)
                  )}
                </p>

                <div class="flex items-center gap-3 mb-6">
                  <InputMenuField
                    type="number"
                    name="accessTokenExpiry"
                    menuFieldName="accessTokenExpiryUnit"
                    min={1}
                    menuItems={ACCESS_TOKEN_UNIT_OPTIONS}
                    onMenuItemClick={(item) => {
                      formContext.setTouched('accessTokenExpiry', true);
                      formContext.setTouched('accessTokenExpiryUnit', true);
                      formContext.setFields('accessTokenExpiryUnit', item);
                    }}
                    onKeyDown={(e) =>
                      preventInvalidKeyPress(e, ['-', '.', '+'])
                    }
                    data-testid={`multi-app-access-token-expiry-input`}
                  />
                </div>
                <div class={`flex gap-2`}>
                  <SubmitButton
                    size={'sm'}
                    data-testid={`multi-app-edit-save-button`}
                  >
                    <Localized
                      translationKey={getTranslationKey('config_cta_save')}
                    />
                  </SubmitButton>
                  <Button
                    size={`sm`}
                    variant={`outline`}
                    type={`button`}
                    disabled={!isChanged()}
                    onClick={() => reset()}
                    data-testid={`multi-app-edit-cancel-button`}
                  >
                    <Localized
                      translationKey={getTranslationKey('cta_cancel')}
                    />
                  </Button>
                </div>
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
}

export default ApplicationDetailsForm;
